/**
 * Diagnosis-card mappings from the eligibility detail patient strip.
 */

import type { PatientDetailResponse } from "../../api/clinicalTrialsApiTypes";
import type { DiagnosisSummary } from "./diagnosis/DiagnosisSummaryRow";

const EMPTY_VALUE = "—";

/**
 * Collect metastatic site labels from either a string list or the structured
 * assessment object the patient API actually returns.
 *
 * @param sites - Patient metastatic-site field
 * @returns Flattened site labels
 */
export function metastaticSiteLabels(sites: unknown): string[] {
  if (Array.isArray(sites)) {
    return sites.filter((site): site is string => typeof site === "string");
  }
  if (sites && typeof sites === "object") {
    const assessment = sites as { confirmed?: string[]; suspected?: string[] };
    return [...(assessment.confirmed ?? []), ...(assessment.suspected ?? [])];
  }
  return [];
}

/**
 * Map one cancer/diagnosis record into the diagnosis-card row shape.
 *
 * @param name - Diagnosis display name
 * @param details - Secondary diagnosis line
 * @param stage - Stage label
 * @param metastaticSites - Site labels
 * @returns Diagnosis summary row
 */
function toDiagnosisSummary(
  name: string,
  details: string,
  stage: string,
  metastaticSites: string[],
): DiagnosisSummary {
  return {
    name: name || EMPTY_VALUE,
    details: details || EMPTY_VALUE,
    stage: stage || EMPTY_VALUE,
    metastaticSites,
  };
}

/**
 * Build diagnosis-card rows from the patient strip.
 *
 * @param patient - Detail `patient` object
 * @returns Primary row plus additional malignancy cards
 */
export function mapEligibilityDiagnoses(patient: PatientDetailResponse): {
  primary: DiagnosisSummary;
  additional: DiagnosisSummary[];
} {
  const sites = metastaticSiteLabels(patient.metastatic_sites);
  const cancers = patient.cancers ?? [];
  if (cancers.length > 0) {
    const [first, ...rest] = cancers;
    return {
      primary: toDiagnosisSummary(
        first.description || patient.diagnosis || EMPTY_VALUE,
        [first.histology, first.diagnosed_on ? `Diagnosed ${first.diagnosed_on}` : ""]
          .filter(Boolean)
          .join(" • ") || EMPTY_VALUE,
        first.stage || patient.stage || EMPTY_VALUE,
        sites,
      ),
      additional: rest.map((cancer) =>
        toDiagnosisSummary(
          cancer.description || cancer.code || EMPTY_VALUE,
          [cancer.histology, cancer.diagnosed_on ? `Diagnosed ${cancer.diagnosed_on}` : ""]
            .filter(Boolean)
            .join(" • ") || EMPTY_VALUE,
          cancer.stage || EMPTY_VALUE,
          sites,
        ),
      ),
    };
  }
  return {
    primary: toDiagnosisSummary(
      patient.diagnosis || EMPTY_VALUE,
      [
        patient.histology,
        patient.diagnosis_date ? `Diagnosed ${patient.diagnosis_date}` : "",
      ]
        .filter(Boolean)
        .join(" • ") || EMPTY_VALUE,
      patient.stage || EMPTY_VALUE,
      sites,
    ),
    additional: [],
  };
}
